import api from './api';
import { getMediaUrl } from '../utils/mediaUrl';

export interface OperatingHoursDto {
  open: string;
  close: string;
}

export interface WeeklyOperatingHoursDto {
  monday?: OperatingHoursDto;
  tuesday?: OperatingHoursDto;
  wednesday?: OperatingHoursDto;
  thursday?: OperatingHoursDto;
  friday?: OperatingHoursDto;
  saturday?: OperatingHoursDto;
  sunday?: OperatingHoursDto;
}

export interface SpecialOperatingDayDto {
  date: Date;
  open: string;
  close: string;
}

export interface ClosedDayDto {
  date: Date;
  reason?: string;
}

export interface SocialMediaDto {
  instagram?: string;
  facebook?: string;
  twitter?: string;
  tiktok?: string;
  website?: string;
}

export interface StockPermissionConfig {
  allowTakeOrReplace: 'all' | 'selected';
  allowedUserIds?: string[];
}

/** Config do estabelecimento na UI (`GET /establishment/:slug` inclui contato e endereço; rotas admin podem acrescentar dados extra). */
export interface EstablishmentConfigResponseDto {
  id?: string;
  fantasyName: string;
  logo?: string;
  primaryColor?: string;
  secondaryColor?: string;
  slogan?: string;
  phone?: string;
  email?: string;
  zipCode?: string;
  street?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  addressNumber?: string;
  complement?: string;
  companyName?: string;
  cnpj?: string;
  timezone?: string;
  weeklyOperatingHours?: WeeklyOperatingHoursDto;
  specialOperatingDays?: SpecialOperatingDayDto[];
  closedDays?: ClosedDayDto[];
  socialMedia?: SocialMediaDto;
  whatsappEnabled?: boolean;
  stockPermissionConfig?: StockPermissionConfig | null;
}

export type EstablishmentMediaType = 'image' | 'video';

export interface PublicWaitingListSummaryItem {
  professionalProfileId: string | null;
  displayName: string;
  count: number;
}

export interface PublicWaitingListSummary {
  date: string;
  totalWaiting: number;
  byProfessional: PublicWaitingListSummaryItem[];
}

export interface EstablishmentGalleryItemDto {
  id: string;
  url: string;
  type: EstablishmentMediaType;
  caption: string | null;
  sortOrder: number;
}

export interface GetEstablishmentBySlugOptions {
  /**
   * Força bypass de cache HTTP (útil quando o estabelecimento acabou de mudar cores/config).
   * Implementado via query param + headers.
   */
  bypassCache?: boolean;
}

/** Evita GET duplicados quando o mesmo slug dispara em paralelo (ex.: React Strict Mode no dev). */
const inflightEstablishmentBySlug = new Map<string, Promise<EstablishmentConfigResponseDto>>();
const inflightGalleryBySlug = new Map<string, Promise<EstablishmentGalleryItemDto[]>>();

/** Unifica camelCase / snake_case e strings vazias na resposta pública do estabelecimento. */
const normalizeEstablishmentPublicPayload = (raw: unknown): EstablishmentConfigResponseDto => {
  const data = raw as EstablishmentConfigResponseDto & {
    primary_color?: string;
    secondary_color?: string;
  };
  const p = data.primaryColor?.trim() || data.primary_color?.trim();
  const s = data.secondaryColor?.trim() || data.secondary_color?.trim();
  return {
    ...data,
    primaryColor: p || data.primaryColor,
    secondaryColor: s || data.secondaryColor,
  };
};

export const publicEstablishmentAPI = {
  getBySlug: async (
    slug: string,
    options?: GetEstablishmentBySlugOptions
  ): Promise<EstablishmentConfigResponseDto> => {
    const bypassCache = options?.bypassCache === true;
    if (!bypassCache) {
      const shared = inflightEstablishmentBySlug.get(slug);
      if (shared) return shared;
    }

    const request = api
      .get(`/establishment/${slug}`, {
        params: bypassCache ? { _ts: Date.now() } : undefined,
        headers: bypassCache ? { 'Cache-Control': 'no-cache' } : undefined,
      })
      .then((response) => normalizeEstablishmentPublicPayload(response.data));

    if (!bypassCache) {
      inflightEstablishmentBySlug.set(slug, request);
      void request.finally(() => {
        inflightEstablishmentBySlug.delete(slug);
      });
    }

    return request;
  },

  getGalleryBySlug: async (slug: string): Promise<EstablishmentGalleryItemDto[]> => {
    const shared = inflightGalleryBySlug.get(slug);
    if (shared) return shared;

    const request = (async (): Promise<EstablishmentGalleryItemDto[]> => {
      try {
        const response = await api.get<
          Array<{
            id: string;
            path: string;
            type: string;
            caption: string | null;
            sortOrder: number;
          }>
        >(`/establishment-media/${slug}/media`);
        const list = response.data ?? [];
        return list.map((item) => ({
          id: item.id,
          url: getMediaUrl(item.path),
          type: (item.type?.toLowerCase() ?? 'image') as EstablishmentMediaType,
          caption: item.caption,
          sortOrder: item.sortOrder,
        }));
      } catch {
        return [];
      }
    })();

    inflightGalleryBySlug.set(slug, request);
    void request.finally(() => {
      inflightGalleryBySlug.delete(slug);
    });

    return request;
  },

  getWaitingListSummary: async (
    slug: string,
    date?: string
  ): Promise<PublicWaitingListSummary | null> => {
    try {
      const response = await api.get<PublicWaitingListSummary>('/waiting-list/public/summary', {
        params: { slug, ...(date ? { date } : {}) },
      });
      return response.data ?? null;
    } catch {
      return null;
    }
  },
};
